// components/kursi-play-v2/KpLiveLessonView.tsx
// =============================================================================
// Kursi Play V2 — Lesson Runtime
//
// State machine: intro → scenario → consequence → reinforcement → transition → summary
//
// The learner:
//   1. Sees intro (situation + role + decision_point)
//   2. Picks one of 3-4 actions
//   3. Sees consequence (what happened)
//   4. Reads reinforcements (principles/tips)
//   5. Proceeds to next scenario
//   6. After all scenarios → navigates to summary route
// =============================================================================

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Easing,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { router, usePathname } from 'expo-router';
import { theme } from '@/constants/theme';
import { getThemeConfig } from '@/constants/kursiPlayThemes';
import { kpLiveApi } from '@/services/kursi-play-v2/kpLiveApiClient';
import { unifiedProgressService } from '@/services/unifiedProgressService';
import { buildRuntimeHomePath, buildRuntimeSummaryPath, resolveRuntimeBase } from '@/features/unified-learning/runtime/runtimeRoutes';
import type {
  KpLessonRuntimeData,
  KpLessonTheory,
  KpScenarioRuntime,
  KpActionRuntime,
  KpSubmitScenarioActionResponse,
  KpLessonRuntimePhase,
  KpScenarioResult,
  KpQualityTier,
  KpSurfaceType,
  KpExtraItem,
} from '@/types/kursi-play-v2';

// ── Colors ────────────────────────────────────────────────────────────────────

const PRIMARY    = theme.colors.primary;
const BG         = '#0F0F1A';
const SURFACE    = '#1E1B3A';
const SURFACE2   = '#2D2B50';
const TEXT       = '#FFFFFF';
const TEXT_MED   = '#A0A0B8';
const TEXT_MUTED = '#6B6B85';
const GREEN      = '#10B981';
const AMBER      = '#F59E0B';
const RED        = '#EF4444';

const TIER_COLORS: Record<KpQualityTier, { bg: string; border: string; text: string }> = {
  strong:     { bg: '#0D3320', border: GREEN,  text: GREEN },
  acceptable: { bg: '#1C2810', border: '#84CC16', text: '#84CC16' },
  weak:       { bg: '#1C1A00', border: AMBER,  text: AMBER },
  risky:      { bg: '#2D0D0D', border: RED,    text: RED },
};

const OUTCOME_COLORS: Record<string, { bg: string; text: string }> = {
  excellent: { bg: '#0D3320', text: GREEN },
  good:      { bg: '#1C2810', text: '#84CC16' },
  acceptable:{ bg: '#1C1A00', text: AMBER },
  poor:      { bg: '#2D0D0D', text: RED },
};

// ── State machine ─────────────────────────────────────────────────────────────

type LessonState = {
  phase:              KpLessonRuntimePhase;
  scenarioIdx:        number;
  reinforcementIdx:   number;
  scenarioResults:    KpScenarioResult[];
  totalXp:            number;
};

type LessonAction =
  | { type: 'CHOOSE_ACTION'; result: KpScenarioResult }
  | { type: 'NEXT_REINFORCEMENT' }
  | { type: 'NEXT_SCENARIO' }
  | { type: 'COMPLETE' };

function reducer(state: LessonState, action: LessonAction): LessonState {
  switch (action.type) {
    case 'CHOOSE_ACTION':
      return {
        ...state,
        phase: 'consequence',
        reinforcementIdx: 0,
        scenarioResults: [...state.scenarioResults, action.result],
        totalXp: state.totalXp + action.result.xp_awarded,
      };
    case 'NEXT_REINFORCEMENT': {
      const currentResult = state.scenarioResults[state.scenarioIdx];
      const reinfCount = currentResult?.reinforcements?.length ?? 0;

      // After consequence, enter reinforcement at index 0 when available.
      if (state.phase !== 'reinforcement') {
        if (reinfCount > 0) {
          return { ...state, phase: 'reinforcement', reinforcementIdx: 0 };
        }
        return { ...state, phase: 'transition', reinforcementIdx: 0 };
      }

      const nextReinfIdx  = state.reinforcementIdx + 1;
      if (nextReinfIdx < reinfCount) {
        return { ...state, reinforcementIdx: nextReinfIdx };
      }
      // Done with reinforcements → transition
      return { ...state, phase: 'transition', reinforcementIdx: 0 };
    }
    case 'NEXT_SCENARIO': {
      const nextIdx = state.scenarioIdx + 1;
      return { ...state, phase: 'scenario', scenarioIdx: nextIdx, reinforcementIdx: 0 };
    }
    case 'COMPLETE':
      return { ...state, phase: 'summary' };
    default:
      return state;
  }
}

// ── Props ─────────────────────────────────────────────────────────────────────

type Props = {
  lessonId: string;
  slug:     string;
  userId:   string;
  guestSessionToken?: string;
};

// ── Main component ────────────────────────────────────────────────────────────

export function KpLiveLessonView({ lessonId, slug, userId, guestSessionToken }: Props) {
  const runtimeSession = useMemo(() => ({ uid: userId, token: guestSessionToken }), [userId, guestSessionToken]);
  const [runtimeData, setRuntimeData] = useState<KpLessonRuntimeData | null>(null);
  const runtimeBase = resolveRuntimeBase(usePathname());
  const [loading, setLoading]         = useState(true);
  const [loadError, setLoadError]     = useState<string | null>(null);
  const [submitting, setSubmitting]   = useState(false);

  // TASK 4: show theory primer before first scenario if lesson has theory data
  const [theoryDismissed, setTheoryDismissed] = useState(false);

  // TASK 11: extra item interleaving (MCQ + flash_concept)
  const [currentExtraItem, setCurrentExtraItem]       = useState<KpExtraItem | null>(null);
  const [extraItemShownCount, setExtraItemShownCount] = useState(0);

  const [state, dispatch] = useReducer(reducer, {
    phase: 'scenario',
    scenarioIdx: 0,
    reinforcementIdx: 0,
    scenarioResults: [],
    totalXp: 0,
  });

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savingComplete, setSavingComplete] = useState(false);
  const completionInFlightRef = useRef(false);

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(30)).current;
  const scenarioStartRef = useRef<number>(Date.now());

  const animateIn = useCallback(() => {
    fadeAnim.setValue(0);
    slideAnim.setValue(20);
    Animated.parallel([
      Animated.timing(fadeAnim,  { toValue: 1, duration: 350, easing: Easing.out(Easing.ease), useNativeDriver: true }),
      Animated.timing(slideAnim, { toValue: 0, duration: 350, easing: Easing.out(Easing.ease), useNativeDriver: true }),
    ]).start();
  }, [fadeAnim, slideAnim]);

  // Load lesson runtime data
  useEffect(() => {
    kpLiveApi.getLessonRuntime(lessonId, slug, userId)
      .then((data) => {
        setRuntimeData(data);
        setLoading(false);
        animateIn();
      })
      .catch((e: unknown) => {
        // TASK 7: structured LESSON_LOCKED error (KpApiError has errorCode property)
        const isLocked = (e instanceof Error && (
          (e as { errorCode?: string }).errorCode === 'LESSON_LOCKED' ||
          e.message === 'LESSON_LOCKED'
        ));
        if (isLocked) {
          setLoadError('__LESSON_LOCKED__');
        } else {
          setLoadError(e instanceof Error ? e.message : 'שגיאה בטעינת שיעור');
        }
        setLoading(false);
      });
  }, [lessonId, slug, userId, animateIn]);

  // Animate when phase changes, and reset think-time timer on new scenario
  useEffect(() => {
    animateIn();
    if (state.phase === 'scenario') {
      scenarioStartRef.current = Date.now();
    }
  }, [state.phase, state.scenarioIdx, state.reinforcementIdx, animateIn]);

  // ── Submit action ──────────────────────────────────────────────────────────
  const handleChooseAction = useCallback(async (scenario: KpScenarioRuntime, action: KpActionRuntime) => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const timeTakenMs = Math.max(0, Date.now() - scenarioStartRef.current);
      const response: KpSubmitScenarioActionResponse = await kpLiveApi.submitScenarioAction({
        scenario_id:   scenario.id,
        action_id:     action.id,
        lesson_id:     lessonId,
        slug,
        user_id:       userId,
        time_taken_ms: timeTakenMs,
      });
      dispatch({
        type: 'CHOOSE_ACTION',
        result: {
          scenario_id:    scenario.id,
          action_id:      action.id,
          quality_tier:   response.quality_tier,
          xp_awarded:     response.xp_awarded,
          consequence:    response.consequence,
          reinforcements: response.reinforcements,
        },
      });
    } catch (e: unknown) {
      setSubmitError(e instanceof Error ? e.message : 'שגיאה בשליחת פעולה');
    } finally {
      setSubmitting(false);
    }
  }, [submitting, lessonId, slug, userId]);

  // ── Complete lesson ────────────────────────────────────────────────────────
  const handleCompleteLesson = useCallback(async () => {
    if (completionInFlightRef.current) return;
    completionInFlightRef.current = true;
    setSavingComplete(true);
    try {
      const result = await unifiedProgressService.completeLesson({
        lessonId,
        slug,
        userId,
        guestSessionToken: guestSessionToken || undefined,
      });
      router.replace(
        buildRuntimeSummaryPath(runtimeBase, slug, lessonId, runtimeSession, {
          xp_earned: result.xp_earned,
          total_world_xp: result.total_world_xp,
          streak_days: result.streak_days,
          next_lesson_id: result.next_lesson_id ?? '',
          is_world_complete: result.is_world_complete,
          // TASK 12: freeze event flags
          freeze_awarded:   result.freeze_awarded   ?? false,
          streak_preserved: result.streak_preserved ?? false,
          freeze_used:      result.freeze_used      ?? false,
        }) as any,
      );
    } catch (e: unknown) {
      setSubmitError(e instanceof Error ? e.message : 'שגיאה בסיום שיעור');
      setSavingComplete(false);
      completionInFlightRef.current = false;
    }
  }, [lessonId, runtimeBase, slug, runtimeSession, userId, guestSessionToken]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <View style={s.centered}>
        <ActivityIndicator color={PRIMARY} size="large" />
        <Text style={s.loadingText}>טוען שיעור...</Text>
      </View>
    );
  }

  if (loadError || !runtimeData) {
    // TASK 7: Locked lesson — structured UI
    if (loadError === '__LESSON_LOCKED__') {
      return (
        <View style={s.centered}>
          <Text style={[s.errEmoji, { fontSize: 40 }]}>🔒</Text>
          <Text style={[s.errText, { fontSize: 17, marginTop: 8 }]}>השיעור נעול</Text>
          <Text style={[s.errText, { fontSize: 13, color: '#9896B4', marginTop: 6, maxWidth: 280, textAlign: 'center' }]}>
            השלם את השיעורים הקודמים כדי לפתוח שיעור זה.
          </Text>
          <Pressable style={[s.retryBtn, { marginTop: 20 }]} onPress={() => {
            if (router.canGoBack()) router.back();
            else router.replace(buildRuntimeHomePath(runtimeBase, slug, runtimeSession) as any);
          }}>
            <Text style={s.retryBtnText}>חזרה לרשימת השיעורים</Text>
          </Pressable>
        </View>
      );
    }

    return (
      <View style={s.centered}>
        <Text style={s.errEmoji}>😕</Text>
        <Text style={s.errText}>{loadError ?? 'שגיאה בטעינה'}</Text>
        <Pressable style={s.retryBtn} onPress={() => {
          if (router.canGoBack()) router.back();
          else router.replace(buildRuntimeHomePath(runtimeBase, slug, runtimeSession) as any);
        }}>
          <Text style={s.retryBtnText}>חזרה</Text>
        </Pressable>
      </View>
    );
  }

  const { lesson, scenarios } = runtimeData;
  const themeConfig    = getThemeConfig(runtimeData.world_visual_theme);
  const worldPrimary   = runtimeData.world_primary_color ?? PRIMARY;
  const totalScenarios = scenarios.length;
  const currentScenario = scenarios[state.scenarioIdx] ?? null;
  const currentResult   = state.scenarioResults[state.scenarioIdx] ?? null;
  const currentReinf    = currentResult?.reinforcements?.[state.reinforcementIdx] ?? null;
  const progressPct     = totalScenarios > 0 ? (state.scenarioIdx / totalScenarios) * 100 : 0;
  const hasValidConsequence = !!(
    currentResult?.consequence
    && typeof currentResult.consequence.narrative === 'string'
    && currentResult.consequence.narrative.trim().length > 0
    && typeof currentResult.consequence.outcome_label === 'string'
    && currentResult.consequence.outcome_label.trim().length > 0
    && typeof currentResult.consequence.outcome_quality === 'string'
  );

  return (
    <View style={s.root}>
      {/* ── Top bar ── */}
      <View style={s.topBar}>
        <Pressable
          onPress={() => {
            if (router.canGoBack()) router.back();
            else router.replace(buildRuntimeHomePath(runtimeBase, slug, runtimeSession) as any);
          }}
          style={s.closeBtn}
          hitSlop={12}
          accessibilityLabel="סגור שיעור"
        >
          <Text style={s.closeBtnText}>✕</Text>
        </Pressable>

        <View style={s.progressBg}>
          <View style={[s.progressFill, { width: `${progressPct}%` as any }]} />
        </View>

        <Text style={s.counterText}>{state.scenarioIdx + 1}/{totalScenarios}</Text>
      </View>

      {/* ── Lesson title ── */}
      <Text style={s.lessonTitle} numberOfLines={2}>{lesson.title}</Text>

      {/* ── Domain strip ── */}
      <View style={[s.domainStrip, { backgroundColor: worldPrimary + '14', borderBottomColor: worldPrimary + '28' }]}>
        <Text style={s.domainStripEmoji}>{themeConfig.environmentEmoji}</Text>
        <Text style={[s.domainStripText, { color: worldPrimary }]}>{themeConfig.environmentLabel}</Text>
        <Text style={s.domainStripHero}>{themeConfig.heroEmoji}</Text>
      </View>

      {/* ── Phase content ── */}
      <Animated.View
        style={[s.phaseArea, { opacity: fadeAnim, transform: [{ translateY: slideAnim }] }]}
      >
        {/* TASK 4: Theory phase — shown before first scenario if theory exists */}
        {!theoryDismissed && runtimeData.lesson.theory && state.phase === 'scenario' && state.scenarioIdx === 0 && (
          <TheoryPhase
            theory={runtimeData.lesson.theory}
            onContinue={() => setTheoryDismissed(true)}
          />
        )}

        {(theoryDismissed || !runtimeData.lesson.theory || state.scenarioIdx > 0) && state.phase === 'scenario' && currentScenario && (
          <ScenarioPhase
            scenario={currentScenario}
            submitting={submitting}
            submitError={submitError}
            onChoose={(action) => handleChooseAction(currentScenario, action)}
          />
        )}

        {state.phase === 'consequence' && currentResult && hasValidConsequence && (
          <ConsequencePhase
            consequence={currentResult.consequence!}
            qualityTier={currentResult.quality_tier}
            xpAwarded={currentResult.xp_awarded}
            onNext={() => dispatch({ type: 'NEXT_REINFORCEMENT' })}
          />
        )}

        {state.phase === 'consequence' && currentResult && !hasValidConsequence && (
          <ConsequenceFallbackPhase
            onContinue={() => dispatch({ type: 'NEXT_REINFORCEMENT' })}
            onExit={() => {
              if (router.canGoBack()) router.back();
              else router.replace(buildRuntimeHomePath(runtimeBase, slug, runtimeSession) as any);
            }}
          />
        )}

        {state.phase === 'reinforcement' && currentReinf && (
          <ReinforcementPhase
            reinf={currentReinf}
            isLast={state.reinforcementIdx >= (currentResult?.reinforcements?.length ?? 0) - 1}
            onNext={() => dispatch({ type: 'NEXT_REINFORCEMENT' })}
          />
        )}

        {state.phase === 'reinforcement' && !currentReinf && (
          <ConsequenceFallbackPhase
            title="לא הצלחנו לטעון את כרטיס החיזוק"
            body="אפשר להמשיך לשלב הבא בבטחה או לחזור למסך הבית."
            continueLabel="המשך"
            onContinue={() => dispatch({ type: 'NEXT_REINFORCEMENT' })}
            onExit={() => {
              if (router.canGoBack()) router.back();
              else router.replace(buildRuntimeHomePath(runtimeBase, slug, runtimeSession) as any);
            }}
          />
        )}

        {state.phase === 'transition' && !currentExtraItem && (
          <TransitionPhase
            scenarioIdx={state.scenarioIdx}
            totalScenarios={totalScenarios}
            totalXp={state.totalXp}
            onNext={() => {
              // TASK 11: interleave extra items between scenarios
              const extraItems = (runtimeData?.lesson.extra_items ?? []);
              if (extraItemShownCount < extraItems.length) {
                setCurrentExtraItem(extraItems[extraItemShownCount]);
                setExtraItemShownCount((n) => n + 1);
              } else if (state.scenarioIdx + 1 < totalScenarios) {
                dispatch({ type: 'NEXT_SCENARIO' });
              } else {
                dispatch({ type: 'COMPLETE' });
              }
            }}
          />
        )}

        {/* TASK 11: MCQ extra item */}
        {currentExtraItem?.type === 'mcq' && (
          <McqPhase
            item={currentExtraItem}
            onDismiss={() => {
              setCurrentExtraItem(null);
              if (state.scenarioIdx + 1 < totalScenarios) {
                dispatch({ type: 'NEXT_SCENARIO' });
              } else {
                dispatch({ type: 'COMPLETE' });
              }
            }}
          />
        )}

        {/* TASK 11: Flash concept extra item */}
        {currentExtraItem?.type === 'flash_concept' && (
          <FlashConceptPhase
            item={currentExtraItem}
            onDismiss={() => {
              setCurrentExtraItem(null);
              if (state.scenarioIdx + 1 < totalScenarios) {
                dispatch({ type: 'NEXT_SCENARIO' });
              } else {
                dispatch({ type: 'COMPLETE' });
              }
            }}
          />
        )}

        {state.phase === 'summary' && (
          <LessonDonePhase
            totalXp={state.totalXp}
            scenarioCount={totalScenarios}
            results={state.scenarioResults}
            saving={savingComplete}
            saveError={submitError}
            onComplete={handleCompleteLesson}
            isGuest={!userId || userId.startsWith('guest_')}
          />
        )}
      </Animated.View>
    </View>
  );
}

function ConsequenceFallbackPhase({
  onContinue,
  onExit,
  title = 'אין נתון תוצאה תקין לתרחיש הזה',
  body = 'אפשר להמשיך בבטחה לתרגול הבא או לצאת למסך הבית ולנסות שוב מאוחר יותר.',
  continueLabel = 'המשך בטוח',
}: {
  onContinue: () => void;
  onExit: () => void;
  title?: string;
  body?: string;
  continueLabel?: string;
}) {
  return (
    <View style={s.fallbackWrap}>
      <Text style={s.fallbackEmoji}>⚠️</Text>
      <Text style={s.fallbackTitle}>{title}</Text>
      <Text style={s.fallbackBody}>{body}</Text>

      <View style={s.fallbackActions}>
        <Pressable style={s.primaryBtn} onPress={onContinue}>
          <Text style={s.primaryBtnText}>{continueLabel}</Text>
        </Pressable>
        <Pressable style={s.secondaryBtn} onPress={onExit}>
          <Text style={s.secondaryBtnText}>יציאה</Text>
        </Pressable>
      </View>
    </View>
  );
}

// ── ScenarioPhase ─────────────────────────────────────────────────────────────

function ScenarioPhase({
  scenario,
  submitting,
  submitError,
  onChoose,
}: {
  scenario:    KpScenarioRuntime;
  submitting:  boolean;
  submitError: string | null;
  onChoose:    (action: KpActionRuntime) => void;
}) {
  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={s.phaseScroll}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
    >
      {/* Template badge */}
      <View style={s.templateBadge}>
        <Text style={s.templateBadgeText}>{TEMPLATE_LABELS[scenario.template_type]}</Text>
      </View>

      {/* Situation */}
      <View style={s.situationCard}>
        <Text style={s.situationLabel}>המצב</Text>
        <Text style={s.situationText}>{scenario.situation}</Text>
        {scenario.role_instruction && (
          <View style={s.roleBox}>
            <Text style={s.roleText}>{scenario.role_instruction}</Text>
          </View>
        )}
      </View>

      {/* Surface (visual aid) */}
      {scenario.surface && (
        <SimulationSurface type={scenario.surface.type} payload={scenario.surface.payload as Record<string, unknown>} />
      )}

      {/* Decision point */}
      <Text style={s.decisionPoint}>{scenario.decision_point}</Text>

      {/* Error */}
      {submitError && (
        <Text style={s.submitError}>{submitError}</Text>
      )}

      {/* Actions */}
      <View style={s.actionsWrap}>
        {scenario.actions.map((action) => (
          <Pressable
            key={action.id}
            style={[s.actionBtn, submitting && s.actionBtnDisabled]}
            onPress={() => onChoose(action)}
            disabled={submitting}
            accessibilityRole="button"
            accessibilityLabel={action.label}
          >
            {submitting ? (
              <ActivityIndicator color={TEXT_MUTED} size="small" />
            ) : (
              <>
                <Text style={s.actionLabel}>{action.label}</Text>
                {action.description && (
                  <Text style={s.actionDesc}>{action.description}</Text>
                )}
              </>
            )}
          </Pressable>
        ))}
      </View>

      <View style={{ height: 40 }} />
    </ScrollView>
  );
}

// ── ConsequencePhase ──────────────────────────────────────────────────────────

function ConsequencePhase({
  consequence,
  qualityTier,
  xpAwarded,
  onNext,
}: {
  consequence:  NonNullable<KpSubmitScenarioActionResponse['consequence']>;
  qualityTier:  KpQualityTier;
  xpAwarded:    number;
  onNext:       () => void;
}) {
  const tierColor    = TIER_COLORS[qualityTier] ?? TIER_COLORS.acceptable;
  const outcomeColor = OUTCOME_COLORS[consequence.outcome_quality] ?? OUTCOME_COLORS.acceptable;

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={s.phaseScroll}
      showsVerticalScrollIndicator={false}
    >
      {/* Outcome badge */}
      <View style={[s.outcomeBadge, { backgroundColor: outcomeColor.bg }]}>
        <Text style={[s.outcomeBadgeText, { color: outcomeColor.text }]}>
          {consequence.outcome_label}
        </Text>
        {xpAwarded > 0 && (
          <Text style={[s.xpBadge, { color: AMBER }]}>+{xpAwarded} XP ⚡</Text>
        )}
      </View>

      {/* Consequence narrative */}
      <View style={s.consequenceCard}>
        <Text style={s.consequenceLabel}>מה קרה</Text>
        <Text style={s.consequenceText}>{consequence.narrative}</Text>
      </View>

      <Pressable style={s.primaryBtn} onPress={onNext}>
        <Text style={s.primaryBtnText}>למד מהתוצאה ⟶</Text>
      </Pressable>

      <View style={{ height: 40 }} />
    </ScrollView>
  );
}

// ── ReinforcementPhase ────────────────────────────────────────────────────────

function ReinforcementPhase({
  reinf,
  isLast,
  onNext,
}: {
  reinf:  NonNullable<KpSubmitScenarioActionResponse['reinforcements']>[0];
  isLast: boolean;
  onNext: () => void;
}) {
  const typeIcon = REINF_ICONS[reinf.reinforcement_type] ?? '💡';

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={s.phaseScroll}
      showsVerticalScrollIndicator={false}
    >
      <View style={s.reinfTypeRow}>
        <Text style={s.reinfTypeIcon}>{typeIcon}</Text>
        <Text style={s.reinfTypeLabel}>{REINF_LABELS[reinf.reinforcement_type]}</Text>
      </View>

      <View style={s.reinfCard}>
        <Text style={s.reinfHeadline}>{reinf.headline}</Text>
        <Text style={s.reinfBody}>{reinf.body}</Text>
        {reinf.source_anchor && (
          <View style={s.reinfSourceBox}>
            <Text style={s.reinfSourceLabel}>מקור</Text>
            <Text style={s.reinfSource}>"{reinf.source_anchor}"</Text>
          </View>
        )}
      </View>

      <Pressable style={s.primaryBtn} onPress={onNext}>
        <Text style={s.primaryBtnText}>{isLast ? 'המשך לתרחיש הבא ⟶' : 'תובנה נוספת ⟶'}</Text>
      </Pressable>

      <View style={{ height: 40 }} />
    </ScrollView>
  );
}

// ── TransitionPhase ───────────────────────────────────────────────────────────

function TransitionPhase({
  scenarioIdx,
  totalScenarios,
  totalXp,
  onNext,
}: {
  scenarioIdx:    number;
  totalScenarios: number;
  totalXp:        number;
  onNext:         () => void;
}) {
  const isLast = scenarioIdx + 1 >= totalScenarios;
  return (
    <View style={[s.phaseScroll, s.transitionCenter]}>
      <Text style={s.transitionEmoji}>{isLast ? '🏁' : '⚡'}</Text>
      <Text style={s.transitionTitle}>
        {isLast ? 'כל התרחישים הושלמו!' : `תרחיש ${scenarioIdx + 1} מתוך ${totalScenarios} הושלם`}
      </Text>
      {totalXp > 0 && (
        <Text style={s.transitionXp}>+{totalXp} XP עד כה</Text>
      )}
      <Pressable style={[s.primaryBtn, { marginTop: 24, width: '100%' }]} onPress={onNext}>
        <Text style={s.primaryBtnText}>
          {isLast ? 'סיים שיעור ✓' : `תרחיש ${scenarioIdx + 2} ⟶`}
        </Text>
      </Pressable>
    </View>
  );
}

// ── LessonDonePhase ───────────────────────────────────────────────────────────

function LessonDonePhase({
  totalXp,
  scenarioCount,
  results,
  saving,
  saveError,
  onComplete,
  isGuest,
}: {
  totalXp:      number;
  scenarioCount: number;
  results:      KpScenarioResult[];
  saving:       boolean;
  saveError:    string | null;
  onComplete:   () => void;
  isGuest:      boolean;
}) {
  const strongCount = results.filter((r) => r.quality_tier === 'strong').length;

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={[s.phaseScroll, s.transitionCenter]}
      showsVerticalScrollIndicator={false}
    >
      <Text style={s.transitionEmoji}>🎉</Text>
      <Text style={s.transitionTitle}>שיעור הושלם!</Text>

      <View style={s.summaryStats}>
        {/* TASK 8: For guests, show "session score" label instead of implying persistent XP */}
        <SummaryRow
          icon="⚡"
          label={isGuest ? 'ניקוד סשן' : 'XP הרווחת'}
          value={`+${totalXp}`}
          note={isGuest ? 'לא נשמר (אורח)' : undefined}
        />
        <SummaryRow icon="🎯" label="תרחישים" value={`${scenarioCount}`} />
        {strongCount > 0 && (
          <SummaryRow icon="💪" label="החלטות מצוינות" value={`${strongCount}`} />
        )}
      </View>

      {/* TASK 8: Guest reminder to sign up */}
      {isGuest && (
        <View style={s.guestXpNote}>
          <Text style={s.guestXpNoteText}>
            🔐 הירשם כדי לשמור את ה-XP וההתקדמות שלך
          </Text>
        </View>
      )}

      {saveError && (
        <Text style={s.submitError}>{saveError}</Text>
      )}

      <Pressable
        style={[s.primaryBtn, { marginTop: 24, width: '100%' }, saving && s.primaryBtnDisabled]}
        onPress={onComplete}
        disabled={saving}
      >
        {saving
          ? <ActivityIndicator color="#fff" size="small" />
          : <Text style={s.primaryBtnText}>{isGuest ? 'המשך →' : 'שמור והמשך ⟶'}</Text>
        }
      </Pressable>

      <View style={{ height: 40 }} />
    </ScrollView>
  );
}

function SummaryRow({ icon, label, value, note }: { icon: string; label: string; value: string; note?: string }) {
  return (
    <View style={s.summaryRow}>
      <View style={{ alignItems: 'flex-end' }}>
        <Text style={s.summaryValue}>{value}</Text>
        {note && <Text style={s.summaryNote}>{note}</Text>}
      </View>
      <Text style={s.summaryLabel}>{label}</Text>
      <Text style={s.summaryIcon}>{icon}</Text>
    </View>
  );
}

// ── TheoryPhase (TASK 4) ──────────────────────────────────────────────────────

function TheoryPhase({ theory, onContinue }: { theory: KpLessonTheory; onContinue: () => void }) {
  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={s.phaseScroll}
      showsVerticalScrollIndicator={false}
    >
      <View style={s.theoryBadge}>
        <Text style={s.theoryBadgeText}>📖 רקע תיאורטי</Text>
      </View>

      <View style={s.theoryCard}>
        <Text style={s.theoryConcept}>{theory.concept_title}</Text>
        <Text style={s.theorySummary}>{theory.summary}</Text>

        {theory.key_points.length > 0 && (
          <View style={s.theoryPoints}>
            {theory.key_points.map((pt, i) => (
              <View key={i} style={s.theoryPointRow}>
                <Text style={s.theoryPointBullet}>•</Text>
                <Text style={s.theoryPointText}>{pt}</Text>
              </View>
            ))}
          </View>
        )}

        {!!theory.connection_to_practice && (
          <View style={s.theoryConnection}>
            <Text style={s.theoryConnectionLabel}>🎯 לפרקטיקה</Text>
            <Text style={s.theoryConnectionText}>{theory.connection_to_practice}</Text>
          </View>
        )}
      </View>

      <Pressable style={s.primaryBtn} onPress={onContinue}>
        <Text style={s.primaryBtnText}>הבנתי, בוא נתרגל ⟶</Text>
      </Pressable>

      <View style={{ height: 40 }} />
    </ScrollView>
  );
}

// ── SimulationSurface ─────────────────────────────────────────────────────────

function SimulationSurface({ type, payload }: { type: KpSurfaceType; payload: Record<string, unknown> }) {
  return (
    <View style={s.surface}>
      <Text style={s.surfaceTitle}>
        {SURFACE_LABELS[type] ?? type}
      </Text>
      <SurfaceContent type={type} payload={payload} />
    </View>
  );
}

function SurfaceContent({ type, payload }: { type: KpSurfaceType; payload: Record<string, unknown> }) {
  switch (type) {
    case 'chart':
      return <ChartSurface payload={payload} />;
    case 'conversation':
      return <ConversationSurface payload={payload} />;
    case 'code':
      return <CodeSurface payload={payload} />;
    case 'document':
      return <DocumentSurface payload={payload} />;
    case 'dashboard':
      return <DashboardSurface payload={payload} />;
    case 'workflow':
      return <WorkflowSurface payload={payload} />;
    default:
      return null;
  }
}

function ChartSurface({ payload }: { payload: Record<string, unknown> }) {
  const labels = (payload.labels as string[]) ?? [];
  const values = (payload.values as number[]) ?? [];
  const maxVal = Math.max(...values, 1);
  const unit   = String(payload.unit ?? '');

  return (
    <View style={s.chartWrap}>
      {!!payload.title && <Text style={s.chartTitle}>{String(payload.title)}</Text>}
      <View style={s.chartBars}>
        {labels.map((label, i) => {
          const val = values[i] ?? 0;
          const pct = (val / maxVal) * 100;
          return (
            <View key={i} style={s.chartBarGroup}>
              <Text style={s.chartBarValue}>{val}{unit}</Text>
              <View style={s.chartBarBg}>
                <View style={[s.chartBarFill, { height: `${pct}%` as any }]} />
              </View>
              <Text style={s.chartBarLabel} numberOfLines={1}>{label}</Text>
            </View>
          );
        })}
      </View>
      {!!payload.caption && <Text style={s.chartCaption}>{String(payload.caption)}</Text>}
    </View>
  );
}

function ConversationSurface({ payload }: { payload: Record<string, unknown> }) {
  const messages = (payload.messages as Array<{ speaker: string; text: string; role?: string }>) ?? [];
  return (
    <View style={s.convWrap}>
      {!!payload.context && <Text style={s.convContext}>{String(payload.context)}</Text>}
      {messages.map((msg, i) => (
        <View key={i} style={s.convMsg}>
          <Text style={s.convSpeaker}>{msg.speaker}</Text>
          <Text style={s.convText}>{msg.text}</Text>
        </View>
      ))}
    </View>
  );
}

function CodeSurface({ payload }: { payload: Record<string, unknown> }) {
  return (
    <View style={s.codeWrap}>
      {!!payload.caption && <Text style={s.codeCaption}>{String(payload.caption)}</Text>}
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <Text style={s.codeText}>{String(payload.code ?? '')}</Text>
      </ScrollView>
      {!!payload.error && (
        <View style={s.codeError}>
          <Text style={s.codeErrorText}>⚠ {String(payload.error)}</Text>
        </View>
      )}
    </View>
  );
}

function DocumentSurface({ payload }: { payload: Record<string, unknown> }) {
  const sections = (payload.sections as Array<{ heading?: string; content: string }>) ?? [];
  return (
    <View style={s.docWrap}>
      {!!payload.title && <Text style={s.docTitle}>{String(payload.title)}</Text>}
      {sections.map((sec, i) => (
        <View key={i} style={s.docSection}>
          {sec.heading && <Text style={s.docHeading}>{sec.heading}</Text>}
          <Text style={s.docContent}>{sec.content}</Text>
        </View>
      ))}
      {!!payload.caption && <Text style={s.docCaption}>{String(payload.caption)}</Text>}
    </View>
  );
}

function DashboardSurface({ payload }: { payload: Record<string, unknown> }) {
  const metrics = (payload.metrics as Array<{ label: string; value: string | number; trend?: string; note?: string }>) ?? [];
  return (
    <View style={s.dashWrap}>
      {!!payload.title && <Text style={s.dashTitle}>{String(payload.title)}</Text>}
      <View style={s.dashMetrics}>
        {metrics.map((m, i) => (
          <View key={i} style={s.dashMetric}>
            <Text style={s.dashValue}>{m.value}{m.trend === 'up' ? ' ↑' : m.trend === 'down' ? ' ↓' : ''}</Text>
            <Text style={s.dashLabel}>{m.label}</Text>
            {m.note && <Text style={s.dashNote}>{m.note}</Text>}
          </View>
        ))}
      </View>
    </View>
  );
}

function WorkflowSurface({ payload }: { payload: Record<string, unknown> }) {
  const steps = (payload.steps as Array<{ label: string; status?: string; detail?: string }>) ?? [];
  const STATUS_ICONS: Record<string, string> = {
    done: '✓', current: '▶', pending: '○', blocked: '✕',
  };
  return (
    <View style={s.workflowWrap}>
      {!!payload.title && <Text style={s.workflowTitle}>{String(payload.title)}</Text>}
      {steps.map((step, i) => (
        <View key={i} style={s.workflowStep}>
          <Text style={s.workflowStepIcon}>{STATUS_ICONS[step.status ?? 'pending'] ?? '○'}</Text>
          <View style={{ flex: 1 }}>
            <Text style={s.workflowStepLabel}>{step.label}</Text>
            {step.detail && <Text style={s.workflowStepDetail}>{step.detail}</Text>}
          </View>
        </View>
      ))}
    </View>
  );
}

// ── Constants ─────────────────────────────────────────────────────────────────

const TEMPLATE_LABELS: Record<string, string> = {
  decision:     '🎯 קבלת החלטה',
  conversation: '💬 שיחה מקצועית',
  analysis:     '🔍 ניתוח מצב',
  fix:          '🛠 תיקון בעיה',
};

const SURFACE_LABELS: Record<string, string> = {
  chart:        '📊 נתונים',
  dashboard:    '📈 לוח מחוונים',
  conversation: '💬 שיחה',
  code:         '💻 קוד',
  document:     '📄 מסמך',
  workflow:     '🔄 תהליך',
};

const REINF_ICONS: Record<string, string> = {
  principle:        '🧠',
  micro_practice:   '🎯',
  mistake_alert:    '⚠️',
  pattern_reminder: '🔁',
};

const REINF_LABELS: Record<string, string> = {
  principle:        'עיקרון מרכזי',
  micro_practice:   'תרגול מיידי',
  mistake_alert:    'שגיאה נפוצה',
  pattern_reminder: 'דפוס לזכור',
};

// ── TASK 11: McqPhase ─────────────────────────────────────────────────────────

function McqPhase({ item, onDismiss }: { item: { type: 'mcq'; question: string; options: string[]; correct_index: number; explanation: string }; onDismiss: () => void }) {
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const revealed = selectedIdx !== null;

  return (
    <ScrollView contentContainerStyle={{ gap: 0 }} showsVerticalScrollIndicator={false}>
      <View style={s.extraCard}>
        <View style={[s.extraTopLine, { backgroundColor: AMBER }]} />
        <View style={s.extraCardBody}>
          <View style={s.extraBadge}>
            <Text style={s.extraBadgeText}>שאלת הבנה 💡</Text>
          </View>
          <Text style={s.extraQuestion}>{item.question}</Text>

          <View style={s.mcqOptions}>
            {item.options.map((opt, idx) => {
              let extraStyle: object | null = null;
              let textColor = TEXT_MED;
              if (revealed) {
                if (idx === item.correct_index)  { extraStyle = s.mcqOptionCorrect; textColor = GREEN; }
                else if (idx === selectedIdx)     { extraStyle = s.mcqOptionWrong;   textColor = RED; }
              } else if (selectedIdx === idx) {
                extraStyle = s.mcqOptionSelected;
                textColor = TEXT;
              }
              return (
                <Pressable
                  key={idx}
                  style={extraStyle ? [s.mcqOption, extraStyle] : s.mcqOption}
                  onPress={() => !revealed && setSelectedIdx(idx)}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: selectedIdx === idx }}
                >
                  <Text style={[s.mcqOptionText, { color: textColor }]}>{opt}</Text>
                </Pressable>
              );
            })}
          </View>

          {revealed && (
            <View style={s.mcqExplanation}>
              <Text style={s.mcqExplanationText}>{item.explanation}</Text>
            </View>
          )}

          {revealed && (
            <Pressable style={s.primaryBtn} onPress={onDismiss} accessibilityRole="button">
              <Text style={s.primaryBtnText}>המשך ←</Text>
            </Pressable>
          )}
        </View>
      </View>
    </ScrollView>
  );
}

// ── TASK 11: FlashConceptPhase ────────────────────────────────────────────────

function FlashConceptPhase({ item, onDismiss }: { item: { type: 'flash_concept'; term: string; definition: string; example: string }; onDismiss: () => void }) {
  return (
    <ScrollView contentContainerStyle={{ gap: 0 }} showsVerticalScrollIndicator={false}>
      <View style={s.extraCard}>
        <View style={[s.extraTopLine, { backgroundColor: PRIMARY }]} />
        <View style={s.extraCardBody}>
          <View style={s.extraBadge}>
            <Text style={s.extraBadgeText}>מושג מפתח 🔑</Text>
          </View>
          <Text style={s.flashTerm}>{item.term}</Text>

          <View style={s.flashSection}>
            <Text style={s.flashSectionLabel}>הגדרה</Text>
            <Text style={s.flashSectionText}>{item.definition}</Text>
          </View>

          <View style={s.flashSection}>
            <Text style={s.flashSectionLabel}>דוגמה</Text>
            <View style={s.flashExampleBox}>
              <Text style={s.flashSectionText}>{item.example}</Text>
            </View>
          </View>

          <Pressable style={s.primaryBtn} onPress={onDismiss} accessibilityRole="button">
            <Text style={s.primaryBtnText}>הבנתי ←</Text>
          </Pressable>
        </View>
      </View>
    </ScrollView>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: BG },

  // Loading/error
  centered: {
    flex: 1, backgroundColor: BG,
    alignItems: 'center', justifyContent: 'center', gap: 12, padding: 32,
  },
  loadingText: { color: TEXT_MUTED, fontFamily: theme.fonts.regular, fontSize: 14 },
  errEmoji:    { fontSize: 44 },
  errText:     { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 16, textAlign: 'center' },
  retryBtn:    { backgroundColor: PRIMARY, borderRadius: 12, paddingVertical: 12, paddingHorizontal: 28, marginTop: 8 },
  retryBtnText:{ color: '#fff', fontFamily: theme.fonts.bold, fontSize: 15 },

  // Top bar
  topBar: {
    flexDirection: 'row-reverse', alignItems: 'center',
    paddingHorizontal: 16, paddingTop: Platform.OS === 'web' ? 20 : 10, paddingBottom: 14,
    gap: 12, backgroundColor: SURFACE,
    borderBottomWidth: 1, borderBottomColor: SURFACE2,
  },
  closeBtn: {
    width: 38, height: 38, borderRadius: 19,
    backgroundColor: SURFACE2,
    borderWidth: 1, borderColor: '#FFFFFF15',
    alignItems: 'center', justifyContent: 'center',
  },
  closeBtnText: { fontSize: 15, color: TEXT_MED, fontWeight: '700' as any },
  progressBg: { flex: 1, height: 6, borderRadius: 99, backgroundColor: SURFACE2, overflow: 'hidden' },
  progressFill: { height: 6, borderRadius: 99, backgroundColor: PRIMARY },
  counterText: { fontFamily: theme.fonts.bold, fontSize: 13, color: TEXT_MUTED, minWidth: 36, textAlign: 'center' },

  // Lesson title
  lessonTitle: {
    fontFamily: theme.fonts.medium, fontSize: 14, color: TEXT_MED,
    textAlign: 'right', paddingHorizontal: 16, paddingVertical: 10,
    backgroundColor: SURFACE, borderBottomWidth: 1, borderBottomColor: SURFACE2,
  },

  // Domain strip
  domainStrip: {
    flexDirection: 'row-reverse' as any,
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 1,
  },
  domainStripEmoji: { fontSize: 16 },
  domainStripText: {
    flex: 1,
    fontFamily: theme.fonts.medium,
    fontSize: 12,
    textAlign: 'right' as any,
    letterSpacing: 0.2,
  },
  domainStripHero: { fontSize: 14 },

  // Phase area
  phaseArea: { flex: 1 },
  phaseScroll: {
    paddingHorizontal: 16, paddingTop: 20, paddingBottom: 48,
    maxWidth: 680, alignSelf: 'center' as any, width: '100%', gap: 16,
  },

  // Template badge
  templateBadge: {
    alignSelf: 'flex-end',
    backgroundColor: `${PRIMARY}1A`, borderRadius: 99,
    paddingHorizontal: 12, paddingVertical: 5,
    borderWidth: 1, borderColor: `${PRIMARY}44`,
  },
  templateBadgeText: { fontFamily: theme.fonts.bold, fontSize: 11, color: PRIMARY },

  // Situation
  situationCard: {
    backgroundColor: SURFACE, borderRadius: 22,
    borderWidth: 1, borderColor: SURFACE2, padding: 20, gap: 12,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 6px 24px rgba(0,0,0,0.45)' } as any) : {}),
  },
  situationLabel: { fontFamily: theme.fonts.bold, fontSize: 11, color: TEXT_MUTED, textAlign: 'right', letterSpacing: 0.5 },
  situationText: {
    fontFamily: theme.fonts.regular, fontSize: 16, color: TEXT,
    textAlign: 'right', lineHeight: 27,
  },
  roleBox: {
    backgroundColor: SURFACE2, borderRadius: 14, padding: 14,
    borderLeftWidth: 3, borderLeftColor: PRIMARY,
  },
  roleText: { fontFamily: theme.fonts.medium, fontSize: 13, color: TEXT_MED, textAlign: 'right', lineHeight: 21 },

  // Decision point
  decisionPoint: {
    fontFamily: theme.fonts.bold, fontSize: 17, color: TEXT,
    textAlign: 'right', lineHeight: 27,
  },

  // Actions
  actionsWrap: { gap: 10 },
  actionBtn: {
    backgroundColor: SURFACE, borderRadius: 18,
    borderWidth: 1.5, borderColor: SURFACE2,
    padding: 18, gap: 5,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 2px 10px rgba(0,0,0,0.35)', transition: 'border-color 0.15s' } as any) : {}),
  },
  actionBtnDisabled: { opacity: 0.45 },
  actionLabel: { fontFamily: theme.fonts.bold, fontSize: 15, color: TEXT, textAlign: 'right' },
  actionDesc:  { fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT_MED, textAlign: 'right', lineHeight: 20 },

  // Error
  submitError: {
    fontFamily: theme.fonts.medium, fontSize: 13, color: RED,
    textAlign: 'center', paddingVertical: 8,
    backgroundColor: '#2D0D0D', borderRadius: 12, paddingHorizontal: 16,
  },

  // Consequence
  outcomeBadge: {
    borderRadius: 20, padding: 18, gap: 6,
    alignItems: 'center',
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 6px 20px rgba(0,0,0,0.35)' } as any) : {}),
  },
  outcomeBadgeText: { fontFamily: theme.fonts.bold, fontSize: 20, textAlign: 'center' },
  xpBadge: { fontFamily: theme.fonts.bold, fontSize: 14 },
  consequenceCard: {
    backgroundColor: SURFACE, borderRadius: 22,
    borderWidth: 1, borderColor: SURFACE2, padding: 20, gap: 12,
  },
  consequenceLabel: { fontFamily: theme.fonts.bold, fontSize: 11, color: TEXT_MUTED, textAlign: 'right', letterSpacing: 0.5 },
  consequenceText: {
    fontFamily: theme.fonts.regular, fontSize: 15, color: TEXT,
    textAlign: 'right', lineHeight: 25,
  },

  // Reinforcement
  reinfTypeRow: { flexDirection: 'row-reverse', alignItems: 'center', gap: 8 },
  reinfTypeIcon: { fontSize: 22 },
  reinfTypeLabel: { fontFamily: theme.fonts.bold, fontSize: 12, color: PRIMARY, letterSpacing: 0.3 },
  reinfCard: {
    backgroundColor: SURFACE, borderRadius: 22,
    borderWidth: 1, borderColor: SURFACE2, padding: 20, gap: 14,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 4px 20px rgba(0,0,0,0.35)' } as any) : {}),
  },
  reinfHeadline: { fontFamily: theme.fonts.bold, fontSize: 17, color: TEXT, textAlign: 'right', lineHeight: 26 },
  reinfBody: {
    fontFamily: theme.fonts.regular, fontSize: 15, color: TEXT_MED,
    textAlign: 'right', lineHeight: 25,
  },
  reinfSourceBox: {
    backgroundColor: SURFACE2, borderRadius: 12, padding: 12, gap: 4,
    borderWidth: 1, borderColor: '#FFFFFF10',
  },
  reinfSourceLabel: { fontFamily: theme.fonts.bold, fontSize: 10, color: TEXT_MUTED, textAlign: 'right', letterSpacing: 0.5 },
  reinfSource: {
    fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_MED,
    textAlign: 'right', fontStyle: 'italic', lineHeight: 19,
  },

  // Transition / done
  transitionCenter: { alignItems: 'center', justifyContent: 'center', flex: 1 },
  transitionEmoji: { fontSize: 60 },
  transitionTitle: {
    fontFamily: theme.fonts.bold, fontSize: 22, color: TEXT,
    textAlign: 'center', marginTop: 10,
  },
  transitionXp: { fontFamily: theme.fonts.bold, fontSize: 16, color: AMBER, marginTop: 6 },

  // Summary stats
  summaryStats: {
    width: '100%', gap: 12,
    borderTopWidth: 1, borderTopColor: SURFACE2,
    paddingTop: 18, marginTop: 8,
  },
  summaryRow: { flexDirection: 'row-reverse', alignItems: 'center', gap: 10 },
  summaryIcon: { fontSize: 18, width: 26, textAlign: 'center' },
  summaryLabel: { flex: 1, fontFamily: theme.fonts.regular, fontSize: 14, color: TEXT_MED, textAlign: 'right' },
  summaryValue: { fontFamily: theme.fonts.bold, fontSize: 17, color: TEXT },
  summaryNote: { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_MUTED, textAlign: 'right' },

  // TASK 8: Guest XP note
  guestXpNote: {
    backgroundColor: '#1A1226', borderRadius: 12, borderWidth: 1,
    borderColor: '#7C3AED44', padding: 12, marginTop: 6, width: '100%',
  },
  guestXpNoteText: {
    fontFamily: theme.fonts.regular, fontSize: 13, color: '#C4B5FD', textAlign: 'center',
  },

  // TASK 4: Theory phase styles
  theoryBadge: {
    alignSelf: 'flex-end',
    backgroundColor: '#1A1226',
    borderRadius: 99, borderWidth: 1, borderColor: '#7C3AED44',
    paddingHorizontal: 12, paddingVertical: 5, marginBottom: 8,
  },
  theoryBadgeText: { fontFamily: theme.fonts.bold, fontSize: 12, color: '#C4B5FD' },
  theoryCard: {
    backgroundColor: SURFACE, borderRadius: 20, borderWidth: 1,
    borderColor: '#7C3AED33', padding: 20, gap: 14,
  },
  theoryConcept: {
    fontFamily: theme.fonts.bold, fontSize: 20, color: TEXT,
    textAlign: 'right', lineHeight: 28,
  },
  theorySummary: {
    fontFamily: theme.fonts.regular, fontSize: 15, color: TEXT_MED,
    textAlign: 'right', lineHeight: 24,
  },
  theoryPoints: { gap: 8, paddingTop: 4, borderTopWidth: 1, borderTopColor: SURFACE2 },
  theoryPointRow: { flexDirection: 'row-reverse', gap: 8, alignItems: 'flex-start' },
  theoryPointBullet: { fontFamily: theme.fonts.bold, fontSize: 16, color: PRIMARY, width: 16, textAlign: 'center' },
  theoryPointText: {
    flex: 1, fontFamily: theme.fonts.regular, fontSize: 14, color: TEXT_MED,
    textAlign: 'right', lineHeight: 22,
  },
  theoryConnection: {
    backgroundColor: `${PRIMARY}12`, borderRadius: 12, padding: 12,
    borderWidth: 1, borderColor: `${PRIMARY}30`, gap: 4,
  },
  theoryConnectionLabel: { fontFamily: theme.fonts.bold, fontSize: 12, color: PRIMARY, textAlign: 'right' },
  theoryConnectionText: {
    fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT_MED,
    textAlign: 'right', lineHeight: 20,
  },

  // Fallbacks
  fallbackWrap: {
    backgroundColor: SURFACE,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: SURFACE2,
    padding: 20,
    gap: 12,
    alignItems: 'center',
  },
  fallbackEmoji: { fontSize: 36 },
  fallbackTitle: { fontFamily: theme.fonts.bold, fontSize: 16, color: TEXT, textAlign: 'center' },
  fallbackBody: {
    fontFamily: theme.fonts.regular,
    fontSize: 14,
    color: TEXT_MED,
    textAlign: 'center',
    lineHeight: 22,
    maxWidth: 300,
  },
  fallbackActions: { width: '100%', gap: 10, marginTop: 4 },
  secondaryBtn: {
    borderRadius: 16,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: SURFACE2,
    backgroundColor: '#FFFFFF08',
  },
  secondaryBtnText: { fontFamily: theme.fonts.medium, fontSize: 14, color: TEXT_MED },

  // Primary button
  primaryBtn: {
    backgroundColor: PRIMARY, borderRadius: 18, paddingVertical: 17,
    alignItems: 'center', justifyContent: 'center',
    ...(Platform.OS === 'web' ? ({ boxShadow: `0 6px 24px ${PRIMARY}55` } as any) : {}),
  },
  primaryBtnDisabled: { opacity: 0.5 },
  primaryBtnText: { fontFamily: theme.fonts.bold, fontSize: 16, color: '#fff' },

  // Simulation surfaces
  surface: {
    backgroundColor: SURFACE2, borderRadius: 16,
    borderWidth: 1, borderColor: SURFACE2, overflow: 'hidden',
  },
  surfaceTitle: {
    fontFamily: theme.fonts.bold, fontSize: 11, color: TEXT_MUTED,
    textAlign: 'right', padding: 10, paddingBottom: 6,
  },

  // Chart
  chartWrap: { padding: 12, gap: 8 },
  chartTitle: { fontFamily: theme.fonts.bold, fontSize: 13, color: TEXT, textAlign: 'right' },
  chartBars: { flexDirection: 'row-reverse', gap: 8, height: 100, alignItems: 'flex-end' },
  chartBarGroup: { flex: 1, alignItems: 'center', gap: 4 },
  chartBarValue: { fontFamily: theme.fonts.bold, fontSize: 10, color: TEXT_MED },
  chartBarBg: { width: '100%', flex: 1, backgroundColor: '#FFFFFF10', borderRadius: 4, overflow: 'hidden', justifyContent: 'flex-end' },
  chartBarFill: { width: '100%', backgroundColor: PRIMARY, borderRadius: 4 },
  chartBarLabel: { fontFamily: theme.fonts.regular, fontSize: 9, color: TEXT_MUTED, textAlign: 'center' },
  chartCaption: { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_MUTED, textAlign: 'right', fontStyle: 'italic' },

  // Conversation
  convWrap: { padding: 12, gap: 8 },
  convContext: { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_MUTED, textAlign: 'right', fontStyle: 'italic' },
  convMsg: { backgroundColor: '#FFFFFF0A', borderRadius: 10, padding: 10, gap: 2 },
  convSpeaker: { fontFamily: theme.fonts.bold, fontSize: 11, color: PRIMARY, textAlign: 'right' },
  convText: { fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT, textAlign: 'right' },

  // Code
  codeWrap: { padding: 12, gap: 6 },
  codeCaption: { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_MUTED, textAlign: 'right' },
  codeText: {
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'Courier',
    fontSize: 12, color: '#A5F3FC', lineHeight: 20,
  },
  codeError: { backgroundColor: '#2D0D0D', borderRadius: 8, padding: 8, marginTop: 4 },
  codeErrorText: { fontFamily: theme.fonts.medium, fontSize: 12, color: RED },

  // Document
  docWrap: { padding: 12, gap: 10 },
  docTitle: { fontFamily: theme.fonts.bold, fontSize: 14, color: TEXT, textAlign: 'right' },
  docSection: { gap: 4 },
  docHeading: { fontFamily: theme.fonts.bold, fontSize: 12, color: TEXT_MED, textAlign: 'right' },
  docContent: { fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT, textAlign: 'right', lineHeight: 20 },
  docCaption: { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_MUTED, textAlign: 'right', fontStyle: 'italic' },

  // Dashboard
  dashWrap: { padding: 12, gap: 10 },
  dashTitle: { fontFamily: theme.fonts.bold, fontSize: 13, color: TEXT, textAlign: 'right' },
  dashMetrics: { flexDirection: 'row-reverse', gap: 8, flexWrap: 'wrap' },
  dashMetric: {
    backgroundColor: '#FFFFFF08', borderRadius: 12, padding: 10, gap: 2,
    minWidth: 80, alignItems: 'center',
  },
  dashValue: { fontFamily: theme.fonts.bold, fontSize: 18, color: TEXT },
  dashLabel: { fontFamily: theme.fonts.regular, fontSize: 10, color: TEXT_MED, textAlign: 'center' },
  dashNote:  { fontFamily: theme.fonts.regular, fontSize: 9, color: TEXT_MUTED, textAlign: 'center' },

  // Workflow
  workflowWrap: { padding: 12, gap: 8 },
  workflowTitle: { fontFamily: theme.fonts.bold, fontSize: 13, color: TEXT, textAlign: 'right' },
  workflowStep: { flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 10 },
  workflowStepIcon: { width: 20, fontFamily: theme.fonts.bold, fontSize: 14, color: PRIMARY, textAlign: 'center' },
  workflowStepLabel: { fontFamily: theme.fonts.medium, fontSize: 13, color: TEXT, textAlign: 'right' },
  workflowStepDetail: { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_MUTED, textAlign: 'right' },

  // TASK 11: MCQ + flash_concept styles
  extraCard: {
    backgroundColor: SURFACE,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: SURFACE2,
    overflow: 'hidden',
    marginHorizontal: 2,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' } as any) : {}),
  },
  extraTopLine: { height: 3, width: '100%' },
  extraCardBody: { padding: 20, gap: 14 },
  extraBadge: {
    alignSelf: 'flex-end' as const,
    borderRadius: 99,
    paddingHorizontal: 10,
    paddingVertical: 4,
    backgroundColor: '#FFFFFF10',
  },
  extraBadgeText: { fontFamily: theme.fonts.medium, fontSize: 11, color: TEXT_MED },
  extraQuestion: { fontFamily: theme.fonts.bold, fontSize: 17, color: TEXT, textAlign: 'right', lineHeight: 26 },
  mcqOptions: { gap: 10 },
  mcqOption: {
    borderRadius: 12, borderWidth: 1.5, borderColor: SURFACE2,
    backgroundColor: SURFACE2, padding: 14,
  },
  mcqOptionSelected: { borderColor: PRIMARY, backgroundColor: `${PRIMARY}18` },
  mcqOptionCorrect: { borderColor: GREEN, backgroundColor: '#0D3320' },
  mcqOptionWrong: { borderColor: RED, backgroundColor: '#2D0D0D' },
  mcqOptionText: { fontFamily: theme.fonts.medium, fontSize: 14, textAlign: 'right' },
  mcqExplanation: {
    borderRadius: 10, padding: 12,
    backgroundColor: '#FFFFFF08', borderWidth: 1, borderColor: GREEN + '44',
  },
  mcqExplanationText: { fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT_MED, textAlign: 'right', lineHeight: 20 },

  flashTerm: { fontFamily: theme.fonts.bold, fontSize: 22, color: TEXT, textAlign: 'right', lineHeight: 30 },
  flashSection: { gap: 6 },
  flashSectionLabel: { fontFamily: theme.fonts.bold, fontSize: 11, color: TEXT_MED, textAlign: 'right', letterSpacing: 0.5 },
  flashSectionText: { fontFamily: theme.fonts.regular, fontSize: 14, color: TEXT, textAlign: 'right', lineHeight: 22 },
  flashExampleBox: {
    borderRadius: 10, padding: 12,
    backgroundColor: '#FFFFFF06', borderWidth: 1, borderColor: SURFACE2,
  },
});
